import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useQuestionsFeed, useSession, useUpdateSession, useCompleteSession, useSubmitAttempt, useCreateShare } from '../lib/api';
import { scrollAppToTop } from '../lib/scroll';
import { motion, AnimatePresence, springs, Pressable, FadeIn, CountUp, Confetti, Skeleton } from '../lib/motion';
import { haptic } from '../lib/haptics';

const ADVANCE_DELAY_CORRECT = 600;
const ADVANCE_DELAY_WRONG = 1300;

/**
 * Quick Memory mode: one-line questions with 4 short tappable answers.
 * Tap, see right or wrong instantly, move on. No explanations; the goal is
 * speed memorization, not analysis.
 */
export function QuickMemoryPage() {
  const { sectionId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const navigate = useNavigate();

  const { data: feed } = useQuestionsFeed({ class_section_id: sectionId });
  const { data: sessionData } = useSession(sessionId);
  const updateSession = useUpdateSession();
  const completeSession = useCompleteSession();
  const submitAttempt = useSubmitAttempt();

  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [streak, setStreak] = useState(0);
  const [bestStreak, setBestStreak] = useState(0);
  const [streakBurst, setStreakBurst] = useState(0);
  const [sessionComplete, setSessionComplete] = useState(false);
  const [sessionQuestionIds, setSessionQuestionIds] = useState<string[] | null>(null);
  const [shareStatus, setShareStatus] = useState<'idle' | 'sharing' | 'copied'>('idle');
  const createShare = useCreateShare();
  const hydratedRef = useRef<string | null>(null);
  const xpRef = useRef(0);
  const startRef = useRef(Date.now());
  const advanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const quickQuestions = useMemo(
    () => (feed?.questions ?? []).filter((q: any) => q.type === 'quick_mcq'),
    [feed?.questions],
  );
  const answeredSet = useMemo(
    () => new Set(feed?.answeredQuestionIds ?? []),
    [feed?.answeredQuestionIds],
  );

  // Hydrate session state from server
  useEffect(() => {
    if (!sessionData?.session) return;
    const s = sessionData.session;
    if (hydratedRef.current === s.id) return;
    hydratedRef.current = s.id;
    setSessionQuestionIds(s.questionIds);
    setIdx(s.currentIndex);
    setCorrectCount(s.correctCount);
    setAnsweredCount(s.questionsAnswered);
    xpRef.current = (s as any).xpEarned ?? 0;
  }, [sessionData]);

  const orderedQuestions = useMemo(() => {
    if (!sessionQuestionIds) return quickQuestions;
    const byId = new Map(quickQuestions.map((q: any) => [q.id, q]));
    return sessionQuestionIds.map(id => byId.get(id)).filter(Boolean) as any[];
  }, [quickQuestions, sessionQuestionIds]);

  const current = orderedQuestions[idx];
  const payload = current?.payload as any;
  const total = orderedQuestions.length;

  // Reset per-question state
  useEffect(() => {
    setPicked(null);
    startRef.current = Date.now();
    scrollAppToTop('smooth');
    return () => { if (advanceRef.current) clearTimeout(advanceRef.current); };
  }, [idx]);

  const handleShare = useCallback(async () => {
    if (!current || shareStatus === 'sharing') return;
    setShareStatus('sharing');
    try {
      const res = await createShare.mutateAsync({ question_id: current.id, share_type: 'social' });
      const url = `https://app.squishyschool.com/q/${res.share_id}`;
      if (navigator.share) {
        await navigator.share({ title: 'Can you get this right in 5 seconds?', url });
      } else {
        await navigator.clipboard.writeText(url);
      }
      setShareStatus('copied');
      setTimeout(() => setShareStatus('idle'), 2000);
    } catch {
      setShareStatus('idle');
    }
  }, [current, shareStatus, createShare]);

  const advance = useCallback((newCorrect: number, newAnswered: number) => {
    const nextIdx = idx + 1;
    if (nextIdx >= total) {
      haptic.success();
      setSessionComplete(true);
      if (sessionId) {
        updateSession.mutate({
          id: sessionId,
          currentIndex: idx,
          correctCount: newCorrect,
          questionsAnswered: newAnswered,
          xpEarned: xpRef.current,
        });
        completeSession.mutate(sessionId);
      }
    } else {
      setIdx(nextIdx);
      if (sessionId) {
        updateSession.mutate({
          id: sessionId,
          currentIndex: nextIdx,
          correctCount: newCorrect,
          questionsAnswered: newAnswered,
          xpEarned: xpRef.current,
        });
      }
    }
  }, [idx, total, sessionId, updateSession, completeSession]);

  const handlePick = useCallback((optionIdx: number) => {
    if (picked !== null || !current) return;
    const isCorrect = optionIdx === payload?.correct;
    setPicked(optionIdx);
    if (isCorrect) haptic.success();
    else haptic.error();

    const newCorrect = correctCount + (isCorrect ? 1 : 0);
    const newAnswered = answeredCount + 1;
    setCorrectCount(newCorrect);
    setAnsweredCount(newAnswered);
    const newStreak = isCorrect ? streak + 1 : 0;
    setStreak(newStreak);
    if (newStreak > bestStreak) setBestStreak(newStreak);
    // Celebrate every 5-in-a-row with a happy buzz + confetti burst
    if (newStreak > 0 && newStreak % 5 === 0) {
      haptic.heavy();
      setStreakBurst((n) => n + 1);
    }

    submitAttempt.mutate({
      questionId: current.id,
      correct: isCorrect,
      firstTry: !answeredSet.has(current.id),
      selectedAnswers: [optionIdx],
      timeMs: Date.now() - startRef.current,
      studySessionId: sessionId,
    }, {
      onSuccess: (result) => { xpRef.current += result.xpEarned + (result.speedBonus ?? 0); },
    });

    advanceRef.current = setTimeout(
      () => advance(newCorrect, newAnswered),
      isCorrect ? ADVANCE_DELAY_CORRECT : ADVANCE_DELAY_WRONG,
    );
  }, [picked, current, payload, correctCount, answeredCount, streak, bestStreak, answeredSet, sessionId, submitAttempt, advance]);

  if (sessionComplete) {
    const accuracy = answeredCount > 0 ? Math.round((correctCount / answeredCount) * 100) : 0;
    const perfect = answeredCount > 0 && correctCount === answeredCount;
    const headline = perfect ? 'Flawless round!' : accuracy >= 80 ? 'On fire!' : accuracy >= 50 ? 'Nice work!' : 'Round complete';
    const subline = perfect
      ? `You nailed all ${answeredCount}. Incredible.`
      : `You answered ${answeredCount} questions.`;
    return (
      <div className="relative min-h-screen bg-slate-50 flex items-center justify-center p-4 overflow-hidden">
        <Confetti count={perfect ? 60 : accuracy >= 50 ? 40 : 24} duration={perfect ? 2 : 1.6} />
        <FadeIn className="w-full max-w-sm">
          <div className="bg-white rounded-3xl shadow-card border border-slate-100 p-7 text-center">
            <motion.div
              initial={{ scale: 0.6, opacity: 0, rotate: -12 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              transition={springs.bouncy}
              className="mx-auto w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-pop"
            >
              <motion.svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-8 w-8 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
                animate={{ rotate: [0, -8, 8, 0] }}
                transition={{ duration: 0.6, delay: 0.25 }}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </motion.svg>
            </motion.div>
            <h1 className="font-display text-2xl font-semibold text-squishy-dark mt-5">{headline}</h1>
            <p className="text-sm text-slate-500 mt-1">{subline}</p>

            <div className="grid grid-cols-3 gap-2.5 mt-6">
              <div className="rounded-2xl bg-green-50 border border-green-200 py-3">
                <div className="text-2xl font-bold text-green-600">{correctCount}</div>
                <div className="text-[11px] font-medium text-slate-500 mt-0.5">Right</div>
              </div>
              <div className="rounded-2xl bg-red-50 border border-red-200 py-3">
                <div className="text-2xl font-bold text-red-500">{answeredCount - correctCount}</div>
                <div className="text-[11px] font-medium text-slate-500 mt-0.5">Wrong</div>
              </div>
              <div className="rounded-2xl bg-amber-50 border border-amber-200 py-3">
                <div className="text-2xl font-bold text-amber-600">{bestStreak}</div>
                <div className="text-[11px] font-medium text-slate-500 mt-0.5">Best streak</div>
              </div>
            </div>

            <div className="mt-6">
              <div className="text-4xl font-bold text-amber-600 tabular-nums">
                <CountUp value={accuracy} />%
              </div>
              <p className="text-xs text-slate-400 mt-1">accuracy this session</p>
            </div>

            {xpRef.current > 0 && (
              <motion.p
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...springs.soft, delay: 0.3 }}
                className="mt-3 text-xs font-semibold text-emerald-600"
              >
                +{xpRef.current} XP earned
              </motion.p>
            )}

            <Pressable
              haptic="medium"
              onClick={() => navigate(`/class/${sectionId}`)}
              className="mt-7 w-full bg-amber-500 text-white py-3.5 rounded-2xl font-semibold text-sm"
            >
              Back to Class
            </Pressable>
          </div>
        </FadeIn>
      </div>
    );
  }

  if (!current) {
    return (
      <div className="min-h-screen bg-slate-50 p-4">
        <div className="max-w-lg mx-auto pt-16 space-y-4">
          <Skeleton className="h-2 w-1/3 mx-auto" />
          <Skeleton className="h-16 rounded-2xl" />
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-[72px] rounded-2xl" />
            <Skeleton className="h-[72px] rounded-2xl" />
            <Skeleton className="h-[72px] rounded-2xl" />
            <Skeleton className="h-[72px] rounded-2xl" />
          </div>
          <p className="text-center text-slate-400 text-sm pt-2">Loading quick questions...</p>
        </div>
      </div>
    );
  }

  const options: string[] = payload?.options ?? [];
  const correctIdx: number = payload?.correct;
  const progressPct = ((idx + 1) / total) * 100;

  return (
    <div className="relative min-h-screen bg-slate-50 pb-20">
      {/* Streak milestone celebration */}
      {streakBurst > 0 && (
        <div className="pointer-events-none fixed inset-0 z-20">
          <Confetti key={streakBurst} count={28} duration={1.3} />
        </div>
      )}

      {/* Header */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-sm border-b px-4 py-2">
        <div className="max-w-lg mx-auto flex items-center gap-2">
          <Pressable
            haptic="light"
            onClick={() => navigate(`/class/${sectionId}`)}
            aria-label="Back to class"
            className="p-2.5 -ml-2 rounded-full text-slate-400 hover:text-slate-600 transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </Pressable>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-squishy-dark leading-tight">Quick Study</div>
            <div className="text-[11px] text-slate-400 tabular-nums">{idx + 1} of {total}</div>
          </div>
          <div className="flex items-center gap-1.5">
            <AnimatePresence>
              {streak >= 3 && (
                <motion.span
                  key={streak}
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: [1, 1.18, 1], opacity: 1 }}
                  exit={{ scale: 0.5, opacity: 0 }}
                  transition={springs.bouncy}
                  className="inline-flex items-center gap-1 text-[11px] font-bold text-white bg-gradient-to-r from-amber-500 to-orange-500 rounded-full px-2.5 py-0.5 tabular-nums shadow-[0_0_0_3px_rgba(251,191,36,0.25)]"
                >
                  <motion.svg
                    className="w-3.5 h-3.5"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    animate={{ rotate: [-8, 8, -8], y: [0, -1, 0] }}
                    transition={{ duration: 0.8, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67zM11.71 19c-1.78 0-3.22-1.4-3.22-3.14 0-1.62 1.05-2.76 2.81-3.12 1.77-.36 3.6-1.21 4.62-2.58.39 1.29.59 2.65.59 4.04 0 2.65-2.15 4.8-4.8 4.8z" />
                  </motion.svg>
                  {streak}
                </motion.span>
              )}
            </AnimatePresence>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-600 bg-green-50 border border-green-200 rounded-full px-2 py-0.5 tabular-nums">
              {correctCount}
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
            </span>
            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-500 bg-red-50 border border-red-200 rounded-full px-2 py-0.5 tabular-nums">
              {answeredCount - correctCount}
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M6 18L18 6M6 6l12 12" /></svg>
            </span>
            <Pressable
              haptic="light"
              onClick={handleShare}
              disabled={shareStatus === 'sharing'}
              aria-label="Share this question"
              title="Share this question"
              className="p-2.5 -mr-1.5 rounded-full text-slate-400 hover:text-amber-500 hover:bg-amber-50 transition-colors"
            >
              {shareStatus === 'copied' ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-[18px] w-[18px] text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-[18px] w-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              )}
            </Pressable>
          </div>
        </div>
        {/* Progress bar */}
        <div className="max-w-lg mx-auto mt-1.5">
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-gradient-to-r from-squishy-gold to-amber-500 rounded-full"
              initial={false}
              animate={{ width: `${progressPct}%` }}
              transition={springs.soft}
            />
          </div>
        </div>
      </div>

      {/* Question area */}
      <div className="max-w-lg mx-auto px-4 pt-8">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={current.id}
            initial={{ opacity: 0, x: 36 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -28, transition: { duration: 0.12 } }}
            transition={springs.snappy}
          >
            {payload?.topic && (
              <div className="text-center mb-3">
                <span className="inline-block text-[10px] font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full uppercase tracking-wider">
                  {payload.topic}
                </span>
              </div>
            )}
            <h1 className="text-xl font-bold text-squishy-dark text-center leading-snug min-h-[3.5rem] flex items-center justify-center">
              {payload?.question}
            </h1>

            {/* Options: 2x2 grid of short tappable answers */}
            <div className="grid grid-cols-2 gap-3 mt-8">
              {options.map((opt, i) => {
                const answered = picked !== null;
                const isCorrectOpt = answered && i === correctIdx;
                const isWrongPick = answered && i === picked && picked !== correctIdx;

                let style = 'border-slate-200 bg-white text-squishy-dark shadow-card';
                if (answered) {
                  if (isCorrectOpt) style = 'border-green-500 bg-green-50 text-green-700 shadow-card';
                  else if (isWrongPick) style = 'border-red-400 bg-red-50 text-red-600 animate-shake';
                  else style = 'border-slate-100 bg-slate-50 text-slate-300';
                }
                return (
                  <motion.button
                    key={i}
                    onClick={() => handlePick(i)}
                    disabled={picked !== null}
                    whileTap={!answered ? { scale: 0.95 } : undefined}
                    animate={isCorrectOpt ? { scale: [1, 1.09, 0.98, 1] } : { scale: 1 }}
                    transition={isCorrectOpt ? { duration: 0.4, ease: 'easeOut' } : springs.snappy}
                    className={`min-h-[72px] px-3 py-4 rounded-2xl border-2 font-semibold text-base leading-tight transition-colors ${style}`}
                  >
                    {opt}
                  </motion.button>
                );
              })}
            </div>

            {/* Feedback flash */}
            <div className="h-12 mt-5 flex items-center justify-center">
              <AnimatePresence>
                {picked !== null && (
                  <motion.span
                    initial={{ opacity: 0, y: 8, scale: 0.9 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={springs.bouncy}
                    className={`inline-flex items-center gap-1.5 text-sm font-bold px-4 py-1.5 rounded-full ${
                      picked === correctIdx
                        ? 'text-green-700 bg-green-50 border border-green-200'
                        : 'text-red-600 bg-red-50 border border-red-200'
                    }`}
                  >
                    {picked === correctIdx ? (
                      <>
                        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                        Correct
                      </>
                    ) : (
                      <>Answer: {options[correctIdx]}</>
                    )}
                  </motion.span>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
